import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import BackButton from '@/components/ui/BackButton'
import BrokerMark from '@/components/ui/BrokerMark'
import BsCalendar, { type BsCalendarValue } from '@/components/ui/BsCalendar'
import CardState from '@/components/ui/CardState'
import ChartTypeToggle from '@/components/ui/ChartTypeToggle'
import Change from '@/components/ui/Change'
import Checkbox from '@/components/ui/Checkbox'
import { SymbolChip, ToggleChip } from '@/components/ui/Chip'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import Field from '@/components/ui/Field'
import GoToLink from '@/components/ui/GoToLink'
import IconTile from '@/components/ui/IconTile'
import MarketStatusPill from '@/components/ui/MarketStatusPill'
import MenuMulti from '@/components/ui/MenuMulti'
import MenuSingle from '@/components/ui/MenuSingle'
import Modal from '@/components/ui/Modal'
import type { MenuOption } from '@/components/ui/MenuItem'
import MovePill from '@/components/ui/MovePill'
import Pagination from '@/components/ui/Pagination'
import PeriodPicker, { type PeriodValue } from '@/components/ui/PeriodPicker'
import PeriodTabs, { type PeriodOption } from '@/components/ui/PeriodTabs'
import QuoteRow from '@/components/ui/QuoteRow'
import Radio from '@/components/ui/Radio'
import RefreshBar from '@/components/ui/RefreshBar'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import SegmentedControl, { type SegmentOption } from '@/components/ui/SegmentedControl'
import SelectTrigger from '@/components/ui/SelectTrigger'
import SoulIcon from '@/components/ui/SoulIcon'
import Spinner from '@/components/ui/Spinner'
import StatStrip, { type StatStripItem } from '@/components/ui/StatStrip'
import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import TermTip from '@/components/ui/TermTip'
import TierPill from '@/components/ui/TierPill'
import TitleRow from '@/components/ui/TitleRow'
import { toast } from '@/components/ui/toast-store'
import Tooltip, { TooltipPanel } from '@/components/ui/Tooltip'
import Toggle from '@/components/ui/Toggle'
import Button from '@/components/ui/Button'
import type { ChartMode } from '@/components/charts/candles'
import { todayAdIso, weekdayOfIso } from '@/lib/bsCalendarMath'
import { money } from '@/lib/money'
import BrokerKitGallery from '@/screens/dev/BrokerKitGallery'
import RestKitGallery from '@/screens/dev/RestKitGallery'
import SignalKitGallery from '@/screens/dev/SignalKitGallery'

export const Route = createFileRoute('/_app/dev/components')({ component: ComponentGalleryScreen })

interface KitRow {
  symbol: string
  price: number
  change: number
}

const KIT_ROWS: readonly KitRow[] = [
  { symbol: 'SAPIL', price: 412.6, change: 14.99 },
  { symbol: 'MEPDL', price: 638, change: 14.98 },
  { symbol: 'ALBSL', price: 1190, change: -4.12 },
]

/** The mock's table head terms: one plain, one inside a sortable head. */
const KIT_COLUMNS: DataTableColumn<KitRow>[] = [
  { id: 'symbol', accessorFn: (row) => row.symbol, header: 'Symbol', enableSorting: false },
  {
    id: 'price',
    accessorFn: (row) => row.price,
    header: () => <TermTip term="price">Price (Rs)</TermTip>,
    meta: { numeric: true },
  },
  {
    id: 'change',
    accessorFn: (row) => row.change,
    header: () => <TermTip term="change">% chg</TermTip>,
    cell: ({ row }) => <Change value={money(row.original.change)} percent />,
    meta: { numeric: true },
    enableSorting: false,
  },
]

const KIT_STATS: readonly StatStripItem[] = [
  { label: 'Turnover', value: 'Rs 4.82 arba', term: 'turnoverToday' },
  { label: 'Shares traded', value: '1.42 Cr kitta', term: 'sharesTraded' },
  { label: 'Transactions', value: '1,48,220', term: 'transactionsToday' },
  { label: 'Total scrips traded', value: '244' },
  { label: 'Total market cap', value: 'Rs 4,312 arba', term: 'totalMarketCap' },
]

const KIT_DAYS: readonly SegmentOption<'1d' | '1w'>[] = [
  { value: '1d', label: '1 day' },
  { value: '1w', label: '1 week' },
]

const KIT_SIZING: readonly SegmentOption<'cap' | 'turnover' | 'volume'>[] = [
  { value: 'cap', label: 'Market cap' },
  { value: 'turnover', label: 'Turnover' },
  { value: 'volume', label: 'Volume' },
]

/** One labelled row of variants, so a specimen always says which state it is. */
function Specimen({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label-s text-muted-foreground">{name}</span>
      <div className="flex flex-wrap items-center gap-4">{children}</div>
    </div>
  )
}

function Section({ title, node, children }: { title: string; node: string; children: ReactNode }) {
  return (
    <section className="border-border-subtle border-t pt-6">
      <div className="mb-4 flex items-baseline gap-3">
        <h2 className="text-h-m">{title}</h2>
        <code className="text-label-s text-muted-foreground">{node}</code>
      </div>
      <div className="flex flex-col gap-5">{children}</div>
    </section>
  )
}

const SECTORS: readonly MenuOption[] = [
  { value: 'banking', label: 'Commercial banks', count: 26 },
  { value: 'hydropower', label: 'Hydropower', count: 91 },
  { value: 'life-insurance', label: 'Life insurance', count: 19 },
  { value: 'microfinance', label: 'Microfinance', count: 54 },
  { value: 'development-banks', label: 'Development banks', count: 17 },
  { value: 'manufacturing', label: 'Manufacturing', count: 12 },
]

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'gainers', label: 'Gainers' },
  { value: 'losers', label: 'Losers' },
  { value: 'turnover', label: 'Turnover' },
]

const PERIOD_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '1w', label: '1 week' },
  { value: '1m', label: '1 month' },
  { value: '3m', label: '3 months' },
] as const

/** Illustrative only — NEPSE's real trading calendar (with its own holidays) is a backend concern; a family screen would pass its own `dayStatus`. */
const marketDayStatus = (iso: string): 'open' | 'closed' => {
  const weekday = weekdayOfIso(iso)
  return weekday === 5 || weekday === 6 ? 'closed' : 'open'
}

const CHART_MODES_DEMO: readonly SegmentOption<ChartMode>[] = [
  { value: 'line', label: 'Line' },
  { value: 'candles', label: 'Candles' },
]

const DATE_RANGE_OPTIONS: readonly SegmentOption<'day' | 'range'>[] = [
  { value: 'day', label: 'One day' },
  { value: 'range', label: 'Date range' },
]

const CHART_PERIOD_OPTIONS: readonly PeriodOption<string>[] = [
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
]

const STAT_STRIP_DEMO: readonly StatStripItem[] = [
  { label: 'Turnover', value: 'Rs 8.79 arba', sub: 'All brokers, today' },
  { label: 'Trades', value: '52,410' },
  { label: 'Advances', value: '146', tone: 'up' },
  { label: 'Declines', value: '78', tone: 'down', info: true },
]

const BROKERS: readonly MenuOption[] = [
  { value: 'b33', label: 'NIBL Ace · Broker 33' },
  { value: 'b58', label: 'Nabil Securities · Broker 58' },
  { value: 'b07', label: 'Opal Securities · Broker 07' },
  { value: 'b45', label: 'Global IME · Broker 45' },
  { value: 'b21', label: 'Sunrise Securities · Broker 21' },
]

/**
 * Every generic component, every variant axis, on one page.
 *
 * Exists because the component pages in Figma are the authority on these and
 * the screens never instanced them — so without a specimen sheet there is
 * nowhere to compare the built component against the drawn one. Reviewing a
 * primitive inside a real screen hides exactly the states that are hardest to
 * reach: mixed checkboxes, a failed refresh bar, an error toast.
 */
function ComponentGalleryScreen() {
  const [toggle, setToggle] = useState(false)
  const [persona, setPersona] = useState('maya')
  const [box, setBox] = useState<boolean | 'mixed'>('mixed')
  const [broker, setBroker] = useState<string | null>('b33')
  const [brokerOpen, setBrokerOpen] = useState(false)
  const [sectors, setSectors] = useState<string[]>(['banking', 'hydropower', 'microfinance'])
  const [sectorsOpen, setSectorsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [wideModalOpen, setWideModalOpen] = useState(false)
  const [underlineTab, setUnderlineTab] = useState('gainers')
  const [pillsTab, setPillsTab] = useState('gainers')
  const [brokerPeriod, setBrokerPeriod] = useState<PeriodValue>({ kind: 'preset', value: 'today' })
  const [brokerPeriodNoCal, setBrokerPeriodNoCal] = useState<PeriodValue>({
    kind: 'preset',
    value: 'today',
  })
  const [floorsheetDay, setFloorsheetDay] = useState<PeriodValue>({
    kind: 'day',
    date: todayAdIso(),
  })
  const [calSingle, setCalSingle] = useState<BsCalendarValue>({
    mode: 'single',
    date: todayAdIso(),
  })
  const [calRange, setCalRange] = useState<BsCalendarValue>({
    mode: 'range',
    start: null,
    end: null,
  })
  const [dateRangeMode, setDateRangeMode] = useState<'day' | 'range'>('day')
  const [chartPeriod, setChartPeriod] = useState('1m')
  const [chartMode, setChartMode] = useState<ChartMode>('line')
  const [notifyByApp, setNotifyByApp] = useState(true)
  const [kitDays, setKitDays] = useState<'1d' | '1w'>('1d')
  const [kitSizing, setKitSizing] = useState<'cap' | 'turnover' | 'volume'>('cap')
  const [kitSector, setKitSector] = useState<string | null>('banking')
  const [kitSectorOpen, setKitSectorOpen] = useState(false)
  const [kitPage, setKitPage] = useState(1)
  const [kitPageSize, setKitPageSize] = useState(10)

  return (
    <div className="flex w-full max-w-225 flex-col gap-8">
      <div>
        <h1 className="text-display-m">Component Gallery</h1>
        <p className="mt-2 text-body-m text-muted-foreground">
          The 22 primitives built from the Figma component pages. Switch the theme to check both.
        </p>
      </div>

      <Section title="TitleRow · BackButton · GoToLink" node="chrome.md §9 · §7">
        <Specimen name="plain — the page's own <h1>, no longer sr-only">
          <TitleRow title="Home" />
        </Specimen>
        <Specimen name="back · eyebrow · inline meta · subtitle · right action">
          <TitleRow
            title="Live market"
            back="/market"
            eyebrow="MARKET"
            meta="NEPSE · 7 Aswin 2083"
            subtitle="Every trade as it happens."
            actions={
              <Button size="sm" variant="secondary">
                Refresh
              </Button>
            }
          />
        </Specimen>
        <Specimen name="BackButton — bare chevron, 32px target">
          <BackButton />
        </Specimen>
        <Specimen name="GoToLink — the icon-only ↗ that replaces 'See all ›' on a card header">
          <div className="flex w-70 items-center justify-between">
            <span className="text-h-s">Popular stocks</span>
            <GoToLink to="/market/movers" aria-label="See all movers" />
          </div>
        </Specimen>
      </Section>

      <Section title="TabBar" node="market-a.md #2 · 4236:104328 · 4236:108727">
        <Specimen name="underline (default) — Floorsheet's By broker / By symbol / All trades">
          <TabBar
            options={TAB_OPTIONS}
            value={underlineTab}
            onChange={setUnderlineTab}
            ariaLabel="Underline demo"
          />
        </Specimen>
        <Specimen name="pills — Movers' 6 tabs, Sectors' 3; same role=tablist, opt in per screen">
          <TabBar
            options={TAB_OPTIONS}
            value={pillsTab}
            onChange={setPillsTab}
            ariaLabel="Pills demo"
            variant="pills"
          />
        </Specimen>
      </Section>

      <Section title="Field" node="22:26 · Shape">
        <Specimen name="pill / boxed / auth">
          <Field shape="pill" placeholder="Placeholder" className="w-65" />
          <Field shape="boxed" placeholder="Placeholder" className="w-65" />
          <Field shape="auth" placeholder="Placeholder" className="w-65" />
        </Specimen>
        <Specimen name="label · required · error · disabled">
          <Field label="Email" required placeholder="you@example.com" className="w-65" />
          <Field
            label="Phone"
            error="Enter a 10-digit mobile number"
            defaultValue="98"
            className="w-65"
          />
          <Field label="Locked" disabled defaultValue="Read only" className="w-65" />
        </Specimen>
        <Specimen name="adornments">
          <Field
            leading={<span className="text-body-m text-muted-foreground">+977</span>}
            placeholder="Mobile number"
            className="w-65"
          />
        </Specimen>
      </Section>

      <Section title="Toggle · Radio · Checkbox" node="21:27 · 22:13 · 2645:7">
        <Specimen name="Toggle — off / on">
          <Toggle checked={false} onCheckedChange={() => undefined} aria-label="Off" />
          <Toggle checked aria-label="On" onCheckedChange={() => undefined} />
          <Toggle checked={toggle} onCheckedChange={setToggle} aria-label="Interactive" />
          <Toggle checked disabled aria-label="Disabled" onCheckedChange={() => undefined} />
        </Specimen>
        <Specimen name="Radio — a real group, arrow keys move between them">
          {['maya', 'prakash', 'sita', 'anil'].map((who) => (
            <label key={who} className="flex items-center gap-2 text-body-m capitalize">
              <Radio
                name="persona"
                value={who}
                checked={persona === who}
                onChange={() => {
                  setPersona(who)
                }}
              />
              {who}
            </label>
          ))}
        </Specimen>
        <Specimen name="Checkbox — off / on / mixed (click mixed: it reports true)">
          <Checkbox checked={false} onCheckedChange={() => undefined} aria-label="Off" />
          <Checkbox checked onCheckedChange={() => undefined} aria-label="On" />
          <Checkbox checked={box} onCheckedChange={setBox} aria-label="Interactive mixed" />
          <Checkbox checked disabled onCheckedChange={() => undefined} aria-label="Disabled" />
        </Specimen>
      </Section>

      <Section title="SelectTrigger + Menus" node="2647:81 · 2647:4 · 2647:24">
        <Specimen name="trigger — resting / open / filled">
          <SelectTrigger label="All sectors" />
          <SelectTrigger label="All sectors" open />
          <SelectTrigger label="Sectors · 3 of 11" filled />
        </Specimen>
        <Specimen name="MenuSingle — searchable, long list (broker picker)">
          <MenuSingle
            options={BROKERS}
            value={broker}
            onChange={setBroker}
            open={brokerOpen}
            onOpenChange={setBrokerOpen}
            label="Broker"
            searchable
            searchPlaceholder="Search"
            trigger={(p) => (
              <SelectTrigger
                {...p}
                open={brokerOpen}
                filled={broker !== null}
                label={BROKERS.find((b) => b.value === broker)?.label ?? 'Any broker'}
              />
            )}
          />
        </Specimen>
        <Specimen name="MenuMulti — counts, Select all, draft until Apply">
          <MenuMulti
            options={SECTORS}
            value={sectors}
            onChange={setSectors}
            open={sectorsOpen}
            onOpenChange={setSectorsOpen}
            label="Sectors"
            trigger={(p) => (
              <SelectTrigger
                {...p}
                open={sectorsOpen}
                filled={sectors.length > 0}
                label={
                  sectors.length > 0
                    ? `Sectors · ${String(sectors.length)} of ${String(SECTORS.length)}`
                    : 'All sectors'
                }
              />
            )}
          />
        </Specimen>
      </Section>

      <Section title="Modal" node="2671:17155 · 2672:10196">
        <Specimen name="picker with a footer note · confirm with no body">
          <Button
            variant="secondary"
            aria-haspopup="dialog"
            onClick={() => {
              setModalOpen(true)
            }}
          >
            Open modal
          </Button>
          <Button
            variant="secondary"
            aria-haspopup="dialog"
            onClick={() => {
              setConfirmOpen(true)
            }}
          >
            Open confirm
          </Button>
        </Specimen>
        <Modal
          open={modalOpen}
          onOpenChange={setModalOpen}
          title="Add to watchlist"
          footerNote="2 selected"
          footer={
            <>
              <Button
                variant="muted"
                onClick={() => {
                  setModalOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setModalOpen(false)
                }}
              >
                Add to Main
              </Button>
            </>
          }
        >
          <Field type="search" aria-label="Search" placeholder="Search a company or symbol" />
        </Modal>
        <Modal
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          role="alertdialog"
          size="sm"
          title="Delete NABIL entry watch?"
          description="Mitra stops watching this price. You can set a new alert any time."
          footer={
            <>
              <Button
                variant="muted"
                onClick={() => {
                  setConfirmOpen(false)
                }}
              >
                Keep it
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmOpen(false)
                }}
              >
                Delete alert
              </Button>
            </>
          }
        />
        <Specimen name="size 640 + a leading icon — Account's Log-out, Portfolio's Create/Delete profile">
          <Button
            variant="secondary"
            aria-haspopup="dialog"
            onClick={() => {
              setWideModalOpen(true)
            }}
          >
            Open wide modal
          </Button>
        </Specimen>
        <Modal
          open={wideModalOpen}
          onOpenChange={setWideModalOpen}
          role="alertdialog"
          size="640"
          icon={
            <IconTile tone="accent" size={56} shape="square">
              <span className="text-h-l">👋</span>
            </IconTile>
          }
          title="Log out of MoneyMitra?"
          description="Your book, watchlist and alerts stay saved. Sign in again with your phone number."
          footer={
            <>
              <Button
                variant="muted"
                onClick={() => {
                  setWideModalOpen(false)
                }}
              >
                Stay signed in
              </Button>
              <Button
                onClick={() => {
                  setWideModalOpen(false)
                }}
              >
                Log out
              </Button>
            </>
          }
        />
      </Section>

      <Section title="Spinner · Toast · RefreshBar" node="2701:18 · 2701:39 · 2705:91">
        <Specimen name="Spinner — sm / md / lg">
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" label="Loading prices" />
        </Specimen>
        <Specimen name="Toast — fires into the host mounted in AppShell">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              toast.info('Prices update at every close')
            }}
          >
            Info
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              toast.success('Saved to your list')
            }}
          >
            Success
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              toast.error('That did not go through', {
                label: 'Retry',
                onClick: () => undefined,
              })
            }}
          >
            Error
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              toast.offline('You are offline. Showing the last close.')
            }}
          >
            Offline
          </Button>
        </Specimen>
        <Specimen name="RefreshBar — refreshing / updated / failed / offline">
          <div className="flex w-full flex-col gap-2">
            <RefreshBar state="refreshing" />
            <RefreshBar state="updated" />
            <RefreshBar state="failed" onRetry={() => undefined} />
            <RefreshBar state="offline" onRetry={() => undefined} />
          </div>
        </Specimen>
      </Section>

      <Section title="EmptyState · CardState" node="4236:96619 · 4243:3144 · 4236:70655 · 2705:48">
        <Specimen name="EmptyState — page · failed / offline / empty / not-found">
          <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-2">
            <EmptyState
              kind="failed"
              size="page"
              onRetry={() => undefined}
              meta="Last updated 03:00 PM, 2 Bhadra 2083"
            />
            <EmptyState kind="offline" size="page" onRetry={() => undefined} />
            <EmptyState
              kind="empty"
              size="page"
              title="No issues yet"
              description="The first MoneyMitra Weekly comes out on Sunday morning."
            />
            <EmptyState
              kind="not-found"
              size="page"
              title="Issue not found"
              description="The link may be wrong, or it was removed."
              action={
                <button type="button" className={stateActionClass('page')}>
                  Browse all issues
                </button>
              }
            />
          </div>
        </Specimen>
        <Specimen name="EmptyState — section · every kind, inside a card (no art)">
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(
              ['empty', 'no-results', 'not-available', 'not-found', 'failed', 'offline'] as const
            ).map((kind) => (
              <div key={kind} className="rounded-md border border-border-subtle">
                <EmptyState
                  kind={kind}
                  query="banks"
                  thing="Per-broker holdings"
                  onRetry={() => undefined}
                />
              </div>
            ))}
          </div>
        </Specimen>
        <Specimen name="EmptyState — section · standalone (framed prop, no card fill) / banner / retrying">
          <div className="flex w-full flex-col gap-4">
            <EmptyState
              kind="empty"
              framed
              title="No alerts set yet"
              description="Open any company, pick a price, and Mitra rings when it is reached."
              action={
                <button type="button" className={stateActionClass('section')}>
                  Browse the market
                </button>
              }
              secondaryAction={
                <button type="button" className={stateActionClass('section', 'secondary')}>
                  New alert
                </button>
              }
            />
            <EmptyState
              layout="banner"
              framed
              eyebrow="Your watchlist"
              title="Nothing followed yet"
              action={
                <button type="button" className={stateActionClass('section')}>
                  Add company
                </button>
              }
              onDismiss={() => undefined}
            />
            <EmptyState kind="failed" framed onRetry={() => undefined} retrying />
          </div>
        </Specimen>
        <Specimen name="EmptyState — inline · compact">
          <div className="grid w-full grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-md border border-border-subtle p-4">
              <EmptyState
                kind="failed"
                size="inline"
                title="These measures did not load."
                description="The rest of this tab is unaffected."
                onRetry={() => undefined}
              />
            </div>
            <div className="rounded-md border border-border-subtle p-4">
              <EmptyState kind="no-results" size="inline" query="nabl" description={null} />
            </div>
            <EmptyState
              kind="offline"
              size="inline"
              framed
              description="These are the stories saved at 3:15 PM. New stories load when you are back online."
            />
            <div className="flex items-center gap-4 rounded-md border border-border-subtle p-4">
              <div className="h-14 w-30">
                <EmptyState
                  kind="failed"
                  size="compact"
                  title="Couldn't load the chart"
                  onRetry={() => undefined}
                />
              </div>
              <div className="h-14 w-30">
                <EmptyState kind="offline" size="compact" onRetry={() => undefined} />
              </div>
            </div>
          </div>
        </Specimen>
        <Specimen name="CardState — loaded / loading / failed / offline / empty / refreshing">
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(['loaded', 'loading', 'failed', 'offline', 'empty', 'refreshing'] as const).map(
              (s) => (
                <div key={s} className="border-border-subtle rounded-md border p-4">
                  <CardState state={s} onRetry={() => undefined}>
                    <div>
                      <div className="text-label-s text-muted-foreground">NEPSE INDEX</div>
                      <div className="text-display-m tabular-nums">2,622.48</div>
                      <div className="text-market-down text-mono-s">−19.34 (−0.73%)</div>
                    </div>
                  </CardState>
                </div>
              ),
            )}
          </div>
        </Specimen>
      </Section>

      <Section title="TierPill · MovePill · BrokerMark" node="18:15 · 20:19 · 23:17">
        <Specimen name="TierPill — Free / Sadasya / Guru">
          <TierPill tier="Free" />
          <TierPill tier="Sadasya" />
          <TierPill tier="Guru" />
        </Specimen>
        <Specimen name="MovePill — direction comes from the sign, never a prop">
          <MovePill value={money('13.90')} percent={money('2.71')} />
          <MovePill value={money('-13.90')} percent={money('-2.71')} />
          <MovePill value={money('0')} percent={money('0')} />
        </Specimen>
        <Specimen name="BrokerMark — sm / md / lg">
          <BrokerMark brokerId={33} size="sm" />
          <BrokerMark brokerId={58} size="md" />
          <BrokerMark brokerId={7} size="lg" />
        </Specimen>
      </Section>

      <Section title="Tooltip" node="3195:2 · Hover states">
        <Specimen name="hover or focus a mark — the panel is the same in both">
          <Tooltip
            title="Nabil Securities in Hydropower"
            rows={[
              { label: 'Bought', value: '21.30 Cr' },
              { label: 'Sold', value: '8.90 Cr' },
              { label: 'Net', value: '+12.40 Cr', tone: 'up' },
            ]}
          >
            <button
              type="button"
              className="rounded-xs bg-market-up-soft px-2 py-1 text-mono-s text-market-up hover:ring-1 hover:ring-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              +12.40
            </button>
          </Tooltip>
          <Tooltip
            title="Small trades, under 50K"
            rows={[
              { label: 'Trades', value: '2,410' },
              { label: 'Share of all trades', value: '56%' },
            ]}
          >
            <button
              type="button"
              className="rounded-xs bg-muted px-2 py-1 text-label-m hover:ring-1 hover:ring-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Trade-size bar
            </button>
          </Tooltip>
          <Tooltip title="Volume — title only, no facts">
            <button
              type="button"
              className="rounded-xs bg-muted px-2 py-1 text-label-m hover:ring-1 hover:ring-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Radar point
            </button>
          </Tooltip>
        </Specimen>
        <Specimen name="TooltipPanel — the same box, unanchored, for a recharts content slot">
          <TooltipPanel
            title="Hydropower"
            rows={[
              { label: 'Bought', value: '8.79 arba, 51%' },
              { label: 'Sold', value: '8.36 arba, 49%' },
            ]}
          />
        </Specimen>
      </Section>

      <Section title="SelectTrigger · slot" node="3167:2 · Compare">
        <Specimen name="the dashed Add-broker card — same button, different box">
          <div className="w-80">
            <SelectTrigger
              variant="slot"
              label="Add broker"
              leading={<Plus className="size-4 shrink-0 text-accent" aria-hidden="true" />}
            />
          </div>
        </Specimen>
      </Section>

      <Section
        title="PeriodPicker · BsCalendar"
        node="chrome.md §8 · 5036:97124 · 5036:97329/97694/98065 · 4243:79415"
      >
        <Specimen name="presets + calendar button, both modes — the 9 Broker screens once dates ship">
          <PeriodPicker
            presets={PERIOD_PRESETS}
            value={brokerPeriod}
            onChange={setBrokerPeriod}
            ariaLabel="Period"
            dayStatus={marketDayStatus}
          />
        </Specimen>
        <Specimen name="presets only, no calendar button — today's actual Brokers: allowCustomDate={false}">
          <PeriodPicker
            presets={PERIOD_PRESETS}
            value={brokerPeriodNoCal}
            onChange={setBrokerPeriodNoCal}
            ariaLabel="Period"
            allowCustomDate={false}
          />
        </Specimen>
        <Specimen name="single day only, no presets, own trigger copy — Floorsheet's date chip">
          <PeriodPicker
            presets={[]}
            value={floorsheetDay}
            onChange={setFloorsheetDay}
            ariaLabel="Trading day"
            calendarMode="single"
            dayTitle="Choose a trading day"
            subtitle="The floorsheet is kept for 3 months."
            size="sm"
          />
        </Specimen>
        <Specimen name="BsCalendar alone — single day / a range mid-pick, both with the trading-day legend">
          <div className="flex flex-wrap items-start gap-8">
            <div className="w-88">
              <BsCalendar value={calSingle} onChange={setCalSingle} dayStatus={marketDayStatus} />
            </div>
            <div className="w-88">
              <BsCalendar value={calRange} onChange={setCalRange} />
            </div>
          </div>
        </Specimen>
      </Section>

      <Section
        title="SegmentedControl · PeriodTabs · ChartTypeToggle"
        node="4236:100828 · 4243:89064"
      >
        <Specimen name="SegmentedControl — Line / Candles, radiogroup with arrows/Home/End">
          <SegmentedControl
            options={CHART_MODES_DEMO}
            value={chartMode}
            onChange={setChartMode}
            ariaLabel="Chart type demo"
          />
        </Specimen>
        <Specimen name="SegmentedControl sm — the toolbar's 36px track, 28px segments">
          <SegmentedControl
            options={CHART_MODES_DEMO}
            value={chartMode}
            onChange={setChartMode}
            ariaLabel="Chart type demo, small"
            size="sm"
          />
        </Specimen>
        <Specimen name="SegmentedControl fill — One day / Date range, stretched to its container">
          <div className="w-70">
            <SegmentedControl
              options={DATE_RANGE_OPTIONS}
              value={dateRangeMode}
              onChange={setDateRangeMode}
              ariaLabel="Date range mode demo"
              fill
            />
          </div>
        </Specimen>
        <Specimen name="PeriodTabs — the chart period row, 1D through All">
          <PeriodTabs
            options={CHART_PERIOD_OPTIONS}
            value={chartPeriod}
            onChange={setChartPeriod}
            ariaLabel="Chart period demo"
          />
        </Specimen>
        <Specimen name="ChartTypeToggle — SegmentedControl plus its optional chart-menu slot">
          <ChartTypeToggle value={chartMode} onChange={setChartMode} />
        </Specimen>
      </Section>

      <Section title="SectionHead · SeeAllLink" node="the 2026-09-28 design refresh">
        <Specimen name="SectionHead — eyebrow, title, meta and a See all action, h2 by default">
          <div className="w-105">
            <SectionHead
              eyebrow="MARKET"
              title="Top movers"
              meta="7 names"
              action={<SeeAllLink to="/market/movers">See all</SeeAllLink>}
            />
          </div>
        </Specimen>
        <Specimen name="SeeAllLink — main (14px) and rail (13px), drawn ▸ underlines on hover/focus">
          <SeeAllLink to="/market/movers">See all</SeeAllLink>
          <SeeAllLink to="/market/movers" size="rail">
            See all
          </SeeAllLink>
        </Specimen>
      </Section>

      <Section title="StatStrip · MarketStatusPill" node="4236:100928 · 5407:5020">
        <Specimen name="StatStrip — one row from @2xl, two across below it, an unavailable ⓘ">
          <div className="w-full max-w-160">
            <StatStrip items={STAT_STRIP_DEMO} />
          </div>
        </Specimen>
        <Specimen name="StatStrip panel — the strip as a panel, padded cells, 20px figures">
          <div className="w-full max-w-160">
            <StatStrip items={STAT_STRIP_DEMO} surface="panel" />
          </div>
        </Specimen>
        <Specimen name="MarketStatusPill — open / closed, with the last-close tooltip">
          <MarketStatusPill />
        </Specimen>
      </Section>

      <Section title="SymbolChip · ToggleChip · SoulIcon" node="4243:93645 · 4243:89064">
        <Specimen name="SymbolChip — a ticker tag that opens its stock, no edge on hover">
          <SymbolChip symbol="NABIL" />
          <SymbolChip symbol="NLIC" />
        </Specimen>
        <Specimen name="ToggleChip — a multi-select row, ✓ repeats the state for the eye">
          <ToggleChip
            pressed={notifyByApp}
            onClick={() => {
              setNotifyByApp((was) => !was)
            }}
          >
            App
          </ToggleChip>
          <ToggleChip pressed={false} onClick={() => undefined}>
            Email
          </ToggleChip>
          <ToggleChip pressed={false} onClick={() => undefined}>
            SMS
          </ToggleChip>
        </Specimen>
        <Specimen name="SoulIcon — sm / md / lg, the illustrated soul/* mascot family">
          <SoulIcon name="home" size="sm" label="Home" />
          <SoulIcon name="market" size="md" label="Live market" />
          <SoulIcon name="basket" size="lg" label="Baskets" />
        </Specimen>
      </Section>

      <Section title="Market kit" node="mm-market-2026-10-28 · COMPARE-ov §2">
        <Specimen name="TermTip — a dotted term; 250ms hover, focus or tap opens the card">
          <p className="text-body-m text-muted-foreground">
            The <TermTip term="turnoverToday">turnover</TermTip> and{' '}
            <TermTip title="Floorsheet" body="A one-off card, passed as title and body.">
              today across NEPSE
            </TermTip>
            .
          </p>
        </Specimen>
        <Specimen name="StatStrip — term labels (Overview's stat strip)">
          <div className="w-full max-w-211">
            <StatStrip items={KIT_STATS} />
          </div>
        </Specimen>
        <Specimen name="DataTable — 12/600 tertiary head, terms in heads (one sortable), row wash">
          <div className="w-full max-w-160">
            <DataTable
              columns={KIT_COLUMNS}
              rows={KIT_ROWS}
              getRowId={(row) => row.symbol}
              caption="Market kit table"
              washRows
            />
            <Pagination
              page={kitPage}
              pageSize={kitPageSize}
              total={280}
              onPageChange={setKitPage}
              onPageSizeChange={setKitPageSize}
              pageSizes={[10, 20, 50]}
            />
          </div>
        </Specimen>
        <Specimen name="QuoteRow — the flat movers row (no head, no card)">
          <div className="flex w-full max-w-100 flex-col">
            {KIT_ROWS.map((row) => (
              <QuoteRow
                key={row.symbol}
                symbol={row.symbol}
                name={`${row.symbol} Limited`}
                price={row.price.toFixed(2)}
                change={<Change value={money(row.change)} percent />}
              />
            ))}
          </div>
        </Specimen>
        <Specimen name="SectionHead — the title as a link (.72 on hover)">
          <div className="w-105">
            <SectionHead
              title="Indices"
              titleTo="/market/indices"
              action={<SeeAllLink to="/market/indices">All indices</SeeAllLink>}
            />
          </div>
        </Specimen>
        <Specimen name="SegmentedControl — quiet (Gainers & Losers) and flat (Heatmap sizing)">
          <SegmentedControl
            options={KIT_DAYS}
            value={kitDays}
            onChange={setKitDays}
            ariaLabel="Period"
            size="sm"
            skin="quiet"
          />
          <SegmentedControl
            options={KIT_SIZING}
            value={kitSizing}
            onChange={setKitSizing}
            ariaLabel="Size tiles by"
            size="sm"
            skin="flat"
          />
        </Specimen>
        <Specimen name="MenuSingle — plain skin (Movers' sector menu)">
          <MenuSingle
            options={SECTORS}
            value={kitSector}
            onChange={setKitSector}
            open={kitSectorOpen}
            onOpenChange={setKitSectorOpen}
            label="Sector"
            searchable={false}
            skin="plain"
            align="end"
            trigger={(triggerProps) => (
              <SelectTrigger
                {...triggerProps}
                label={SECTORS.find((sector) => sector.value === kitSector)?.label ?? 'All sectors'}
                open={kitSectorOpen}
              />
            )}
          />
        </Specimen>
      </Section>

      <BrokerKitGallery />

      <SignalKitGallery />

      <RestKitGallery />
    </div>
  )
}
